import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * TopBar
 *
 * The profile menu surfaces membership status and the card on file, then hands
 * off to Account → Billing for anything that changes money. Cancelling from a
 * header menu is a one-misclick mistake, so that action lives on the billing
 * page behind a dialog that states the cancellation terms.
 */

export default function TopBar({ setSidebarOpen, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [billing, setBilling] = useState({ loading: true });
  const dropdownRef = useRef(null);

  const currentPath = location.pathname.replace(/\/$/, "");

  const PAGE_TITLES = {
    "/user-dashboard": "Overview",
    "/my-jobs": "My Jobs",
    "/saved-loads": "Saved Loads",
    "/load-board": "Load Board",
    "/invoices": "Invoices",
    "/companies": "Companies",
    "/messages": "Messages",
    "/settings": "Settings",
  };

  const pageTitle = PAGE_TITLES[currentPath] || "Dashboard";

  const driverName = user?.first_name
    ? `${user.first_name} ${user.last_name ? user.last_name.charAt(0) + "." : ""}`.trim()
    : "Loading...";

  const driverInitials =
    user?.initials || (user?.first_name ? user.first_name.charAt(0).toUpperCase() : "");

  // Close on outside click and on Escape.
  useEffect(() => {
    function onPointerDown(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    function onKeyDown(event) {
      if (event.key === "Escape") setIsProfileOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const fetchBilling = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/billing/active-method/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("unavailable");
      const data = await res.json();
      setBilling({ loading: false, ...data });
    } catch {
      setBilling({ loading: false, unavailable: true });
    }
  }, []);

  useEffect(() => {
    if (isProfileOpen && billing.loading) fetchBilling();
  }, [isProfileOpen, billing.loading, fetchBilling]);

  const goBilling = () => {
    setIsProfileOpen(false);
    navigate("/settings?tab=billing");
  };

  const membershipLabel = () => {
    if (billing.cancel_at_period_end) return "Cancels at period end";
    return {
      trialing: "Trial in progress",
      active: "Pro membership active",
      past_due: "Payment failed",
      unpaid: "Payment failed",
      canceled: "Membership ended",
      none: "No active membership",
    }[billing.membership_status] || "No active membership";
  };

  const membershipTone = () => {
    if (billing.cancel_at_period_end) return "text-amber-600";
    if (["past_due", "unpaid"].includes(billing.membership_status)) return "text-red-600";
    if (["active", "trialing"].includes(billing.membership_status)) return "text-green-600";
    return "text-gray-500";
  };

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center px-3 sm:px-5 gap-3 flex-shrink-0 z-30">
      <button
        onClick={() => setSidebarOpen(true)}
        className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-lg lg:hidden"
        aria-label="Open navigation"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      <h1 className="text-lg font-bold text-gray-900">{pageTitle}</h1>

      {/* Search */}
      <div className="flex-1 max-w-sm mx-4 hidden sm:flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          placeholder="Search jobs, certs, companies..."
          className="flex-1 bg-transparent text-sm text-gray-600 placeholder-gray-300 outline-none min-w-0"
        />
      </div>

      <div className="ml-auto flex items-center gap-1 sm:gap-3">
        {/* Notifications */}
        <button className="relative w-9 h-9 hover:bg-gray-100 rounded-xl flex items-center justify-center text-gray-500" aria-label="Notifications">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span className="absolute top-1.5 right-1.5 w-3.5 h-3.5 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
            2
          </span>
        </button>

        {/* Messages */}
        <button
          onClick={() => navigate("/messages")}
          className="w-9 h-9 hover:bg-gray-100 rounded-xl items-center justify-center text-gray-500 hidden sm:flex"
          aria-label="Messages"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>

        {/* Find Loads CTA */}
        <button
          onClick={() => {
            navigate("/load-board");
            setSidebarOpen(false);
          }}
          className="hidden sm:flex items-center gap-1.5 text-white text-xs font-bold px-3 py-2 rounded-xl"
          style={{ background: "#1a2744" }}
        >
          📋 Find Loads
        </button>

        {/* Profile menu */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsProfileOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={isProfileOpen}
            className="flex items-center gap-2 rounded-xl px-1 py-1 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
              {driverInitials}
            </div>
            <div className="hidden md:flex flex-col items-start">
              <p className="text-sm font-semibold text-gray-800 leading-tight">{driverName}</p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                <span className="text-xs text-green-600 font-medium">On Route</span>
              </div>
            </div>
          </button>

          {isProfileOpen && (
            <div
              role="menu"
              className="absolute right-0 mt-3 w-72 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden"
            >
              {/* Identity */}
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : driverName}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>

              {/* Membership + card */}
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-100 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs font-semibold ${membershipTone()}`}>
                    {billing.loading ? "Checking membership…" : membershipLabel()}
                  </span>
                  <button
                    onClick={goBilling}
                    className="text-xs font-bold text-blue-600 hover:text-blue-700"
                  >
                    Manage
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-6 bg-white border border-gray-200 rounded flex items-center justify-center">
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  {billing.loading ? (
                    <span className="text-sm text-gray-400">Loading card…</span>
                  ) : billing.last4 ? (
                    <span className="text-sm font-medium text-gray-800">
                      •••• {billing.last4}
                    </span>
                  ) : (
                    <button onClick={goBilling} className="text-sm text-blue-600 font-medium hover:text-blue-700">
                      Add a card
                    </button>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="py-2">
                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    navigate("/settings");
                  }}
                  role="menuitem"
                  className="w-full text-left px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Account settings
                </button>
                <button
                  onClick={goBilling}
                  role="menuitem"
                  className="w-full text-left px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Billing and membership
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
